import { useMutation } from '@apollo/react-hooks';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { Input } from 'components/FormFields';
import { VERIFY_EMAIL } from '../../gql/promoter';
import ProgressHeader from '../components/ProgressHeader';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: #94a3b8;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const EmailStatusWrapper = styled.div`
  position: relative;
`;

const EmailStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  color: ${(props) =>
    props.status === 'checking'
      ? '#94a3b8'
      : props.status === 'valid'
      ? '#22c55e'
      : '#ef4444'};
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  button {
    width: auto;
    min-width: 140px;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const UserInfoStep = () => {
  const { formData, updateField, errors, getTotalSteps, getStepNumber } =
    useSignup();
  const history = useHistory();
  const [verifyEmail] = useMutation(VERIFY_EMAIL);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailValid, setEmailValid] = useState(null); // null = not checked, true = valid, false = invalid
  const [emailError, setEmailError] = useState('');
  const debounceRef = useRef(null);

  const isFormValid =
    formData.first_name &&
    formData.last_name &&
    formData.email &&
    formData.zipcode &&
    formData.user_role &&
    emailValid === true &&
    !emailChecking;

  // Debounced email verification
  const checkEmail = useCallback(
    (email) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email || !emailRegex.test(email)) {
        setEmailValid(null);
        setEmailError('');
        setEmailChecking(false);
        return;
      }

      setEmailChecking(true);
      setEmailValid(null);
      setEmailError('');

      debounceRef.current = setTimeout(async () => {
        try {
          await verifyEmail({ variables: { email } });
          setEmailValid(true);
          setEmailError('');
        } catch (err) {
          setEmailValid(false);
          setEmailError(
            err?.graphQLErrors?.[0]?.message || 'This email is not available'
          );
        } finally {
          setEmailChecking(false);
        }
      }, 600);
    },
    [verifyEmail]
  );

  // Verify email when it's populated (handles page reload / navigating back)
  useEffect(() => {
    if (formData.email) {
      checkEmail(formData.email);
    }
  }, [formData.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    updateField('email', email);
    checkEmail(email);
  };

  const handleContinue = () => {
    if (!isFormValid) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Navigate to next step
    history.push('/promoter-signup/business-details');
  };

  const handleBack = () => {
    history.push('/promoter-signup/verify');
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <ProgressHeader
            currentStep={getStepNumber('/promoter-signup/user-info')}
            totalSteps={getTotalSteps()}
            stepTitle="Create Your Account"
          />

          <Card>
            <CardSubtitle>
              We didn't find an existing account for your phone number. Please
              provide your information below to create a user account.
            </CardSubtitle>

            <FormGrid>
              <GridRow>
                <Input
                  id="first_name"
                  label="First Name"
                  placeholder="John"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  error={!!errors.first_name}
                  helperText={errors.first_name}
                  required
                />

                <Input
                  id="last_name"
                  label="Last Name"
                  placeholder="Doe"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  error={!!errors.last_name}
                  helperText={errors.last_name}
                  required
                />
              </GridRow>

              <GridRow>
                <EmailStatusWrapper>
                  <Input
                    id="email"
                    label="Email Address"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleEmailChange}
                    error={
                      !!(errors.email || (emailValid === false && emailError))
                    }
                    helperText={
                      errors.email || (emailValid === false ? emailError : '')
                    }
                    required
                  />
                  {emailChecking && (
                    <EmailStatus status="checking">Verifying...</EmailStatus>
                  )}
                  {emailValid === true && !emailChecking && (
                    <EmailStatus status="valid">Email is available</EmailStatus>
                  )}
                </EmailStatusWrapper>

                <Input
                  id="zipcode"
                  label="Zip Code"
                  placeholder="12345"
                  value={formData.zipcode}
                  onChange={(e) => updateField('zipcode', e.target.value)}
                  error={!!errors.zipcode}
                  helperText={errors.zipcode}
                  required
                />
              </GridRow>

              <Input
                id="user_role"
                label="Role with Organization"
                placeholder="e.g. Owner, Marketing Director, General Manager"
                value={formData.user_role || ''}
                onChange={(e) => updateField('user_role', e.target.value)}
                required
              />
            </FormGrid>

            <ButtonRow>
              <ButtonV2 onClick={handleBack}>← Back</ButtonV2>
              <ButtonV2
                onClick={handleContinue}
                disabled={!isFormValid}
                primary={!!isFormValid}
              >
                Continue →
              </ButtonV2>
            </ButtonRow>
          </Card>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default UserInfoStep;
